/**
 * Test History Summary Component
 *
 * Displays recent CAT assessments with link to full history
 */

import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { accountService } from '../../services/accountService';

interface AssessmentSummary {
  id: string;
  date: string;
  status: 'completed' | 'in-progress';
  title?: string;
  educationLevel?: string;
}

export function TestHistorySummary() {
  const { t } = useTranslation();
  const [assessments, setAssessments] = useState<AssessmentSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAssessments = async () => {
      try {
        const data = await accountService.getRecentAssessments(10);
        setAssessments(data);
      } catch (error) {
        console.error('Failed to load assessments:', error);
      } finally {
        setLoading(false);
      }
    };
    loadAssessments();
  }, []);

  if (loading) {
    return (
      <section className='bg-surface-light dark:bg-surface-dark rounded-xl border border-border-light dark:border-border-dark p-6 shadow-sm'>
        <h3 className='text-lg font-bold text-text-main dark:text-text-main-dark flex items-center gap-2 mb-6'>
          <span className='material-symbols-outlined text-primary'>history</span>
          {t('account.history.title')}
        </h3>
        <div className='flex items-center justify-center py-8'>
          <div className='text-text-secondary dark:text-text-secondary-dark'>
            {t('common.loading')}
          </div>
        </div>
      </section>
    );
  }

  if (assessments.length === 0) {
    return (
      <section className='bg-surface-light dark:bg-surface-dark rounded-xl border border-border-light dark:border-border-dark p-6 shadow-sm'>
        <h3 className='text-lg font-bold text-text-main dark:text-text-main-dark flex items-center gap-2 mb-6'>
          <span className='material-symbols-outlined text-primary'>history</span>
          {t('account.history.title')}
        </h3>
        <div className='flex flex-col items-center justify-center py-8 space-y-4'>
          <span className='material-symbols-outlined text-4xl text-text-secondary dark:text-text-secondary-dark'>
            history
          </span>
          <p className='text-text-secondary dark:text-text-secondary-dark'>
            {t('account.history.empty')}
          </p>
          <Link
            to='/assessment-history'
            className='text-primary hover:text-primary/80 font-medium text-sm'
          >
            {t('account.history.viewFull')}
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className='bg-surface-light dark:bg-surface-dark rounded-xl border border-border-light dark:border-border-dark p-6 shadow-sm'>
      <div className='flex items-center justify-between mb-6'>
        <h3 className='text-lg font-bold text-text-main dark:text-text-main-dark flex items-center gap-2'>
          <span className='material-symbols-outlined text-primary'>history</span>
          {t('account.history.title')}
        </h3>
        <Link
          to='/assessment-history'
          className='text-sm text-primary hover:text-primary/80 font-medium'
        >
          {t('account.history.viewAll')}
        </Link>
      </div>

      <div className='space-y-3'>
        {assessments.map(assessment => (
          <div
            key={assessment.id}
            className='flex items-center justify-between p-3 rounded-lg border border-border-light dark:border-border-dark hover:bg-background-light dark:hover:bg-background-dark transition-colors'
          >
            <div className='flex-1'>
              <p className='text-sm font-medium text-text-main dark:text-text-main-dark'>
                Bài đánh giá CAT {assessment.educationLevel ? `(${assessment.educationLevel})` : ''}
              </p>
              <p className='text-xs text-text-secondary dark:text-text-secondary-dark mt-1'>
                {new Date(assessment.date).toLocaleDateString('vi-VN', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
              </p>
            </div>
            <span
              className={`px-2 py-1 text-xs font-semibold rounded-full ${
                assessment.status === 'completed'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
              }`}
            >
              {assessment.status === 'completed'
                ? t('account.history.completed')
                : t('account.history.inProgress')}
            </span>
          </div>
        ))}
      </div>

      <div className='mt-4 pt-4 border-t border-border-light dark:border-border-dark'>
        <Link
          to='/assessment-history'
          className='block text-center text-sm text-primary hover:text-primary/80 font-medium'
        >
          {t('account.history.viewFull')} →
        </Link>
      </div>
    </section>
  );
}
